import type { ActionFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { networkAPI, APIError } from "../../lib/api";
import { createNetworkSchema, updateNetworkSchema } from "../../schemas";
import { extractFieldErrors, parseFormData } from "../../utils";
import type { ActionResult } from "./auth.actions";

export async function createNetworkAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { id: orgId } = params;

  if (!orgId) {
    return { success: false, error: "Organization ID is required" };
  }

  const formData = await parseFormData(request);

  const result = createNetworkSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const network = await networkAPI.create(orgId, {
      name: result.data.name,
      slug:
        result.data.slug ||
        result.data.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, ""),
      description: result.data.description || undefined,
      logoUrl: result.data.logoUrl || undefined,
      membershipType: result.data.membershipType,
    });
    return redirect(`/organizations/${orgId}/networks/${network.id}/settings`);
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function updateNetworkAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, networkId } = params;

  if (!orgId || !networkId) {
    return { success: false, error: "Organization ID and Network ID are required" };
  }

  const formData = await parseFormData(request);

  const result = updateNetworkSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const network = await networkAPI.update(orgId, networkId, {
      name: result.data.name,
      slug: result.data.slug || undefined,
      description: result.data.description || undefined,
      logoUrl: result.data.logoUrl || undefined,
      membershipType: result.data.membershipType,
    });
    return { success: true, data: network };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function deleteNetworkAction({
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { id: orgId, networkId } = params;

  if (!orgId || !networkId) {
    return { success: false, error: "Organization ID and Network ID are required" };
  }

  try {
    await networkAPI.delete(orgId, networkId);
    return redirect(`/organizations/${orgId}/networks`);
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}
